import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastMessageOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckIcon, ExclamationTriangleIcon, InfoCircleIcon, TimesCircleIcon, TimesIcon } from 'primeng/icons';
import { TemporalContextService } from '../../services/temporal-context.service';
import { ActionToastAction } from './action-toast.service';

/**
 * The single toast mount's headless faces. PrimeNG's headless template replaces the ENTIRE
 * message body, so this component carries an explicit stock-face reproduction for the app's
 * ordinary toasts — the p-toast-message-* class names and severity icon switch byte-match the
 * installed PrimeNG source, so every theme rule (and the global align-items override, which
 * matches the class name whoever renders it) keeps applying; no alignment CSS is re-declared
 * here — plus the ACTION face for toasts carrying a data.action: severity icon + one detail
 * sentence + a text action button + close, no summary line (the ruled undo-toast anatomy).
 *
 * Known non-reproductions of the stock face (all unused across the app's raise sites today):
 * message.icon custom-icon override, closable:false, contentStyleClass, closeIcon.
 * message.styleClass survives — it binds on PrimeNG's own wrapper outside this template.
 *
 * The COUNTDOWN is face-owned: action toasts raise sticky (PrimeNG's internal timer and its
 * full-length restart-on-leave are unreachable from the headless outlet), and this component
 * runs data.life with pause-then-RESUME while the face is held — hovered OR carrying focus
 * (a keyboard member Shift+Tabbing to the button must never have the timer expire under
 * their focus) — cancels it on action click, and dismisses through closeFn when it runs out.
 *
 * Keyboard honesty: the mount precedes the app content in DOM order, so forward-Tab from a
 * grid never reaches the button and the toast deliberately steals no focus (autofocus on the
 * reproduced close button is a post-load-inserted node — browsers ignore it — kept only for
 * stock parity); Shift+Tab reaches it. For TRASH deletes the keyboard-first undo remains the
 * member's journal until the app-wide shortcut lane ships an undo shortcut; for the Manual
 * group-removal family — which never narrates — this toast is the only undo door.
 */
@Component({
  selector: 'app-toast-faces',
  standalone: true,
  imports: [TranslateModule, ButtonModule, CheckIcon, ExclamationTriangleIcon, InfoCircleIcon, TimesCircleIcon, TimesIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './toast-faces.component.html',
})
export class ToastFacesComponent implements OnInit, OnDestroy {
  private readonly temporal = inject(TemporalContextService);

  readonly message = input.required<ToastMessageOptions>();
  readonly closeFn = input.required<(event: Event) => void>();

  protected readonly busy = signal(false);
  private countdown: ReturnType<typeof setTimeout> | null = null;
  private countdownEndsAt = 0;
  private remainingOnPause: number | null = null;
  private destroyed = false;

  protected get action(): ActionToastAction | undefined {
    return (this.message().data as { action?: ActionToastAction } | undefined)?.action;
  }

  /** The stock face stamps the severity as a bare data-p attribute; both faces reproduce it. */
  protected get severityDataP(): string {
    return this.message().severity ?? '';
  }

  protected get detailKey(): string {
    return (this.message().data as { detailKey?: string }).detailKey ?? '';
  }

  protected get detailParams(): Record<string, unknown> | undefined {
    return (this.message().data as { params?: Record<string, unknown> }).params;
  }

  /** A pre-resolved sentence (the raiser translated it already) renders verbatim and wins over the key. */
  protected get detail(): string | undefined {
    return (this.message().data as { detail?: string }).detail;
  }

  ngOnInit(): void {
    if (this.action) {
      const life = (this.message().data as { life?: number }).life ?? 0;
      if (life > 0) {
        this.startCountdown(life);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelCountdown();
  }

  // ── the hold: paused while hovered OR carrying focus; resumes only when BOTH are gone ──

  private hovered = false;
  private focusWithin = false;

  protected onHoverStart(): void {
    this.hovered = true;
    this.pauseCountdown();
  }

  protected onHoverEnd(): void {
    this.hovered = false;
    this.maybeResume();
  }

  protected onFocusIn(): void {
    this.focusWithin = true;
    this.pauseCountdown();
  }

  protected onFocusOut(event: FocusEvent): void {
    // focusout bubbles for every focus move INSIDE the face too (Undo → close);
    // only focus actually leaving the face releases the hold.
    const next = event.relatedTarget;
    if (next instanceof Node && (event.currentTarget as Node).contains(next)) {
      return;
    }
    this.focusWithin = false;
    this.maybeResume();
  }

  private pauseCountdown(): void {
    if (this.countdown !== null) {
      this.remainingOnPause = Math.max(0, this.countdownEndsAt - Date.now());
      this.cancelCountdown();
    }
  }

  private maybeResume(): void {
    if (this.hovered || this.focusWithin) {
      return;
    }
    if (this.remainingOnPause !== null && !this.busy()) {
      this.startCountdown(this.remainingOnPause);
      this.remainingOnPause = null;
    }
  }

  protected async onActionClick(): Promise<void> {
    const action = this.action;
    if (!action || this.busy()) {
      return;
    }
    // The click claims the toast: no timer may tear it down under the in-flight work.
    this.cancelCountdown();
    this.remainingOnPause = null;

    const fenceGeneration = (this.message().data as { fenceGeneration?: number }).fenceGeneration;
    if (fenceGeneration !== undefined && fenceGeneration !== this.temporal.generation()) {
      // The mode changed since the raise — what the button would act on is another world's
      // state. No-op, dismiss; the mode's own messaging already told the user what changed.
      this.dismiss();
      return;
    }

    this.busy.set(true);
    try {
      await action.run();
    } catch {
      // Feedback belongs to the action's own error path (its raiser toasts the failure);
      // this face only stops pretending the offer is still open.
    } finally {
      this.busy.set(false);
      this.dismiss();
    }
  }

  protected onClose(event: Event): void {
    this.cancelCountdown();
    this.closeFn()(event);
  }

  private startCountdown(life: number): void {
    this.cancelCountdown();
    this.countdownEndsAt = Date.now() + life;
    this.countdown = setTimeout(() => this.dismiss(), life);
  }

  private cancelCountdown(): void {
    if (this.countdown !== null) {
      clearTimeout(this.countdown);
      this.countdown = null;
    }
  }

  private dismiss(): void {
    this.cancelCountdown();
    if (this.destroyed) {
      return; // PrimeNG already tore the item down (clear-all) — closing again would throw
    }
    // closeFn preventDefaults unconditionally, so it needs a real event object.
    this.closeFn()(new Event('close'));
  }
}
